// src/components/BDAddressFields.js
// Simplified address fields:
// Free text: House, Road, Moholla, Additional Area, Thana, ZIP
// Searchable dropdown + N/A + manual: Union, Upazila
// Searchable dropdown: District, Division
// Dropdown: Country

import { useState, useEffect, useRef } from 'react'
import {
  BD_DIVISIONS,
  getDistrictsByDivision,
  getUpazilasByDistrict,
  getUnionsByUpazila,
  getAllUnionsByDistrict,
} from '../utils/bdLocations'

const BLUE   = 'var(--primary, #1877F2)'
const TEXT   = 'var(--text-primary, #050505)'
const MUTED  = 'var(--text-muted, #65676B)'
const BORDER = 'var(--border, #E4E6EB)'
const BG     = 'var(--bg-muted, #F0F2F5)'
const WHITE  = 'var(--bg-card, #FFFFFF)'
const ERROR  = '#DC2626'

const COUNTRIES = [
  'Bangladesh','Australia','United States','United Kingdom','Canada','India',
  'Singapore','Malaysia','UAE','Saudi Arabia','Germany','France','Japan',
  'China','Sri Lanka','Nepal','Pakistan','Indonesia','Philippines','Thailand',
  'Vietnam','South Korea','Turkey','Egypt','Nigeria','South Africa','Brazil',
  'New Zealand','Other',
]

// ── Searchable dropdown ───────────────────────────────────────────────────
function SearchDrop({ label, value, onChange, options, placeholder, required, disabled, hint, allowOther = false, allowNA = false }) {
  const [search, setSearch] = useState('')
  const [open, setOpen]     = useState(false)
  const ref = useRef()

  const filtered = search.trim()
    ? options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()))
    : options

  const selectedLabel =
    value === '__other__' ? 'Not in list — type below' :
    value === '__na__'    ? 'N/A — not applicable' :
    options.find(o => o.id === value)?.name || ''

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch('') } }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const pick = (id, name) => { onChange(id, name); setOpen(false); setSearch('') }

  return (
    <div>
      <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px', fontFamily: 'var(--font-ui, Space Grotesk, sans-serif)' }}>
        {label}{required && <span style={{ color: ERROR, marginLeft: '2px' }}>*</span>}
      </label>
      <div style={{ position: 'relative' }} ref={ref}>
        <button type="button" disabled={disabled} onClick={() => !disabled && setOpen(o => !o)}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: `1px solid ${open ? BLUE : BORDER}`, borderRadius: '9px', padding: '8px 11px', fontSize: '12.5px', textAlign: 'left', background: disabled ? BG : WHITE, color: disabled ? MUTED : (selectedLabel ? TEXT : MUTED), cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body, sans-serif)' }}>
          <span>{selectedLabel || placeholder}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={MUTED} strokeWidth="2.5" strokeLinecap="round" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {open && (
          <div style={{ position: 'absolute', left: 0, top: 'calc(100% + 4px)', background: WHITE, borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: `1px solid ${BORDER}`, zIndex: 999, width: '100%', maxHeight: '300px', overflow: 'hidden' }}>
            <div style={{ padding: '8px', borderBottom: `1px solid ${BORDER}` }}>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search..." autoFocus
                style={{ width: '100%', border: `1px solid ${BORDER}`, borderRadius: '8px', padding: '6px 10px', fontSize: '12px', outline: 'none', background: BG, fontFamily: 'var(--font-body, sans-serif)', boxSizing: 'border-box' }}
                onFocus={e => e.target.style.borderColor = BLUE}
                onBlur={e => e.target.style.borderColor = BORDER} />
            </div>
            <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
              {allowNA && (
                <button type="button" onClick={() => pick('__na__', 'N/A')}
                  style={{ width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: '12px', border: 'none', borderBottom: `1px solid ${BORDER}`, background: WHITE, color: MUTED, cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = BG}
                  onMouseLeave={e => e.currentTarget.style.background = WHITE}>
                  N/A — not applicable
                </button>
              )}
              {allowOther && (
                <button type="button" onClick={() => pick('__other__', 'Other')}
                  style={{ width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: '12px', border: 'none', borderBottom: `1px solid ${BORDER}`, background: WHITE, color: MUTED, cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = BG}
                  onMouseLeave={e => e.currentTarget.style.background = WHITE}>
                  Not in list — type below
                </button>
              )}
              {filtered.length === 0
                ? <div style={{ padding: '16px', textAlign: 'center', fontSize: '12px', color: MUTED }}>No results</div>
                : filtered.map(o => (
                  <button key={o.id} type="button" onClick={() => pick(o.id, o.name)}
                    style={{ width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: '12px', border: 'none', background: o.id === value ? BG : WHITE, color: o.id === value ? TEXT : '#374151', fontWeight: o.id === value ? 600 : 400, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onMouseEnter={e => { if (o.id !== value) e.currentTarget.style.background = BG }}
                    onMouseLeave={e => { if (o.id !== value) e.currentTarget.style.background = WHITE }}>
                    <span>{o.name}</span>
                    {o.id === value && <span style={{ color: BLUE, fontSize: '11px' }}>✓</span>}
                  </button>
                ))
              }
            </div>
          </div>
        )}
      </div>
      {hint && <div style={{ fontSize: '11px', color: MUTED, marginTop: '3px' }}>{hint}</div>}
    </div>
  )
}

// ── Text input ─────────────────────────────────────────────────────────────
function TxtInput({ label, value, onChange, placeholder, required, hint, optional }) {
  return (
    <div>
      <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px', fontFamily: 'var(--font-ui, sans-serif)' }}>
        {label}
        {required && <span style={{ color: ERROR, marginLeft: '2px' }}>*</span>}
        {optional && <span style={{ fontWeight: 400, color: '#B0B3B8', marginLeft: '4px' }}>optional</span>}
      </label>
      <input type="text" value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: '100%', border: `1px solid ${BORDER}`, borderRadius: '9px', padding: '8px 11px', fontSize: '12.5px', outline: 'none', background: WHITE, color: TEXT, fontFamily: 'var(--font-body, sans-serif)', boxSizing: 'border-box' }}
        onFocus={e => e.target.style.borderColor = BLUE}
        onBlur={e => e.target.style.borderColor = BORDER} />
      {hint && <div style={{ fontSize: '11px', color: MUTED, marginTop: '3px' }}>{hint}</div>}
    </div>
  )
}

// ── Courier address formatter (exported for use in other components) ────────
export function formatAddressForCourier(address, customerName = '') {
  const clean = (v) => (!v || ['N/A','n/a','__na__','__other__',''].includes((v+'').trim())) ? null : (v+'').trim()
  const parts = [
    customerName || null,
    clean(address.house)   ? `House: ${clean(address.house)}`   : null,
    clean(address.road)    ? `Road: ${clean(address.road)}`     : null,
    clean(address.moholla) ? clean(address.moholla)             : null,
    clean(address.extra)   ? clean(address.extra)               : null,
    clean(address.thana)   ? clean(address.thana)               : null,
    clean(address.zip)     ? `ZIP: ${clean(address.zip)}`       : null,
    clean(address.union_name) && !['__na__','__other__'].includes(address.union_id) ? clean(address.union_name) : null,
    clean(address.district) ? clean(address.district)           : null,
    clean(address.division) ? clean(address.division)           : null,
    clean(address.country)  ? clean(address.country)            : null,
  ].filter(Boolean)
  return parts.join(', ')
}

// ── Main component ─────────────────────────────────────────────────────────
function BDAddressFields({ address, onChange, isBD = true }) {
  const [unions, setUnions]       = useState([])
  const [loadingUnions, setLoadingUnions] = useState(false)

  const divisions  = BD_DIVISIONS
  const districts  = address.division_id && !['__other__','__na__'].includes(address.division_id)
    ? getDistrictsByDivision(address.division_id) : []
  const upazilas   = address.district_id && !['__other__','__na__'].includes(address.district_id)
    ? getUpazilasByDistrict(address.district_id) : []

  const upazilaIsNA    = address.thana_id === '__na__'
  const upazilaIsOther = address.thana_id === '__other__'
  const unionIsOther   = address.union_id === '__other__'

  // Load unions when upazila or district changes
  useEffect(() => {
    if (!address.district_id || ['__other__','__na__'].includes(address.district_id)) { setUnions([]); return }
    setLoadingUnions(true)
    const load = upazilaIsNA
      ? getAllUnionsByDistrict(address.district_id)
      : (address.thana_id && !upazilaIsOther && !upazilaIsNA)
        ? getUnionsByUpazila(address.thana_id)
        : Promise.resolve([])
    load.then(data => { setUnions(data || []); setLoadingUnions(false) })
  }, [address.thana_id, address.district_id, upazilaIsNA, upazilaIsOther])

  // Cascade resets
  const handleDivision = (id, name) => {
    onChange('division_id', id); onChange('division', name)
    onChange('district_id', ''); onChange('district', '')
    onChange('thana_id', '');    onChange('thana', '')
    onChange('union_id', '');    onChange('union_name', '')
  }
  const handleDistrict = (id, name) => {
    onChange('district_id', id); onChange('district', name)
    onChange('thana_id', '');    onChange('thana', '')
    onChange('union_id', '');    onChange('union_name', '')
  }
  const handleUpazila = (id, name) => {
    onChange('thana_id', id); onChange('thana', name)
    onChange('union_id', ''); onChange('union_name', '')
  }
  const handleUnion = (id, name) => { onChange('union_id', id); onChange('union_name', name) }

  // Courier preview with prefixes — clean means skip N/A and empty
  const clean = (v) => (!v || ['N/A','n/a','__na__','__other__',''].includes((v+'').trim())) ? null : (v+'').trim()
  const thanaDisplay = clean(address.metro_thana) || clean(address.thana) || null
  const previewParts = [
    clean(address.house)      ? `House: ${clean(address.house)}`   : null,
    clean(address.road)       ? `Road: ${clean(address.road)}`     : null,
    clean(address.moholla)    ? clean(address.moholla)             : null,
    clean(address.extra)      ? clean(address.extra)               : null,
    thanaDisplay              ? `Thana: ${thanaDisplay}`           : null,
    clean(address.zip)        ? `ZIP: ${clean(address.zip)}`       : null,
    clean(address.union_name) && !['__na__','__other__'].includes(address.union_id) ? clean(address.union_name) : null,
    clean(address.district)   ? clean(address.district)            : null,
    clean(address.division)   ? clean(address.division)            : null,
    clean(address.country)    ? clean(address.country)             : null,
  ].filter(Boolean)

  // ── International — simplified universal address ────────────────────────
  if (!isBD) {
    const cleanIntl = (v) => (!v || (v+'').trim() === '') ? null : (v+'').trim()
    const intlPreview = [
      cleanIntl(address.address_line1),
      cleanIntl(address.address_line2),
      cleanIntl(address.city),
      cleanIntl(address.state),
      cleanIntl(address.zip),
      cleanIntl(address.country),
    ].filter(Boolean)

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <TxtInput label="Address Line 1" value={address.address_line1} onChange={v => onChange('address_line1', v)}
          placeholder="e.g. 45 George Street, Unit 3" required />

        <TxtInput label="Address Line 2" value={address.address_line2 || ''} onChange={v => onChange('address_line2', v)}
          placeholder="Apartment, suite, building (optional)" optional />

        <TxtInput label="City" value={address.city} onChange={v => onChange('city', v)}
          placeholder="e.g. Sydney, New York, London" required />

        <TxtInput label="State / Province" value={address.state} onChange={v => onChange('state', v)}
          placeholder="e.g. NSW, California, Ontario" required />

        <TxtInput label="ZIP / Postcode" value={address.zip} onChange={v => onChange('zip', v)}
          placeholder="e.g. 2000, 10001" required />

        {/* Country dropdown — same as BD branch */}
        <div>
          <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px', fontFamily: 'var(--font-ui, sans-serif)' }}>
            Country <span style={{ color: ERROR, marginLeft: '2px' }}>*</span>
          </label>
          <select value={address.country || ''} onChange={e => onChange('country', e.target.value)}
            style={{ width: '100%', border: `1px solid ${BORDER}`, borderRadius: '9px', padding: '8px 11px', fontSize: '12.5px', outline: 'none', background: WHITE, color: TEXT, fontFamily: 'var(--font-body, sans-serif)', boxSizing: 'border-box', cursor: 'pointer' }}
            onFocus={e => e.target.style.borderColor = BLUE}
            onBlur={e => e.target.style.borderColor = BORDER}>
            <option value="">Select country...</option>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Courier preview */}
        {intlPreview.length >= 2 && (
          <div style={{ borderRadius: '10px', border: `2px solid ${BLUE}`, background: '#E7F3FF', padding: '12px 14px', marginTop: '4px' }}>
            <div style={{ fontSize: '11.5px', fontWeight: 700, color: TEXT, marginBottom: '5px' }}>
              📦 Address preview — double-check before proceeding
            </div>
            <div style={{ fontSize: '12.5px', fontWeight: 500, color: TEXT, lineHeight: 1.7 }}>
              {intlPreview.join(', ')}
            </div>
            <div style={{ fontSize: '11.5px', color: '#DC2626', marginTop: '6px', fontWeight: 500 }}>
              ⚠ If anything looks wrong — correct the fields above before saving.
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── Bangladesh ────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

      {/* ── Free text fields — top section ── */}
      <TxtInput label="House / Flat No" value={address.house} onChange={v => onChange('house', v)}
        placeholder="e.g. House 119/1, Flat 4B" required />

      <TxtInput label="Road / Street No or Name" value={address.road} onChange={v => onChange('road', v)}
        placeholder="e.g. 8  or  Mirpur Road" required />

      <TxtInput label="Moholla / Village / Suburb" value={address.moholla} onChange={v => onChange('moholla', v)}
        placeholder="e.g. Tejkunipara, Kazipara, Mirzapur Village" required />

      <TxtInput label="Additional Area / Section" value={address.extra || ''} onChange={v => onChange('extra', v)}
        placeholder="e.g. Mirpur 10, Section 6, Block C" optional
        hint="Sub-areas, DOHS blocks, road sections — leave blank if not applicable" />

      <TxtInput label="Thana" value={address.metro_thana || address.thana || ''} onChange={v => { onChange('metro_thana', v); onChange('thana', v) }}
        placeholder="e.g. Tejgaon, Gulshan, Mirpur" required />

      <TxtInput label="ZIP / Postcode" value={address.zip} onChange={v => onChange('zip', v)}
        placeholder="e.g. 1215, 3160" optional />

      {/* ── Dropdowns — bottom section ── */}
      <div style={{ height: '1px', background: BORDER, margin: '4px 0' }} />
      <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        Select from lists below
      </div>

      {/* Union */}
      <SearchDrop
        label="Union"
        value={address.union_id || ''}
        onChange={handleUnion}
        options={loadingUnions ? [] : unions}
        placeholder={loadingUnions ? 'Loading...' : 'Select union — or N/A'}
        allowNA allowOther
        hint="Select N/A for Dhaka metro and major city areas"
      />
      {unionIsOther && (
        <TxtInput label="Union — type manually" value={address.union_name || ''} onChange={v => onChange('union_name', v)}
          placeholder="Type union name" required />
      )}

      {/* Upazila */}
      <SearchDrop
        label="Upazila"
        value={address.thana_id || ''}
        onChange={handleUpazila}
        options={upazilas}
        placeholder={address.district_id ? 'Select upazila — or N/A' : 'Select district first'}
        disabled={!address.district_id || ['__other__','__na__'].includes(address.district_id)}
        allowNA allowOther
        hint="Select N/A for Dhaka city, Chattogram city and major district centres"
      />
      {upazilaIsOther && (
        <TxtInput label="Upazila — type manually" value={address.thana || ''} onChange={v => onChange('thana', v)}
          placeholder="Type upazila name" required />
      )}

      {/* District */}
      <SearchDrop
        label="District"
        value={address.district_id || ''}
        onChange={handleDistrict}
        options={districts}
        placeholder={address.division_id ? 'Select district...' : 'Select division first'}
        disabled={!address.division_id || ['__other__','__na__'].includes(address.division_id)}
        required
      />

      {/* Division */}
      <SearchDrop
        label="Division / State"
        value={address.division_id || ''}
        onChange={handleDivision}
        options={divisions}
        placeholder="Select division..."
        required
        allowOther
      />
      {address.division_id === '__other__' && (
        <TxtInput label="Division — type manually" value={address.division} onChange={v => onChange('division', v)}
          placeholder="Type division or state name" required />
      )}

      {/* Country */}
      <div>
        <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px', fontFamily: 'var(--font-ui, sans-serif)' }}>
          Country <span style={{ color: ERROR, marginLeft: '2px' }}>*</span>
        </label>
        <select value={address.country || 'Bangladesh'} onChange={e => onChange('country', e.target.value)}
          style={{ width: '100%', border: `1px solid ${BORDER}`, borderRadius: '9px', padding: '8px 11px', fontSize: '12.5px', outline: 'none', background: WHITE, color: TEXT, fontFamily: 'var(--font-body, sans-serif)', boxSizing: 'border-box', cursor: 'pointer' }}
          onFocus={e => e.target.style.borderColor = BLUE}
          onBlur={e => e.target.style.borderColor = BORDER}>
          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* ── Courier preview ── */}
      {previewParts.length >= 2 && (
        <div style={{ borderRadius: '10px', border: `2px solid ${BLUE}`, background: '#E7F3FF', padding: '12px 14px', marginTop: '4px' }}>
          <div style={{ fontSize: '11.5px', fontWeight: 700, color: TEXT, marginBottom: '5px' }}>
            📦 Courier address preview — double-check before proceeding
          </div>
          <div style={{ fontSize: '12.5px', fontWeight: 500, color: TEXT, lineHeight: 1.7 }}>
            {previewParts.join(', ')}
          </div>
          <div style={{ fontSize: '11.5px', color: '#DC2626', marginTop: '6px', fontWeight: 500 }}>
            ⚠ If anything looks wrong — correct the fields above before saving.
          </div>
        </div>
      )}
    </div>
  )
}

export default BDAddressFields